import React, { Suspense, Fragment, useContext, useEffect } from "react";
import { Router, Switch, Route } from "react-router-dom";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import { routes } from "src/routes";
import { createBrowserHistory } from "history";
import AuthContext from "src/context/Auth";
import PageLoading from "src/component/PageLoading";
import SettingsContext from "src/context/SettingsContext";
import AuthGuard from "src/component/AuthGuard";
import { ThemeProvider } from "@material-ui/core";
import { createMuiTheme } from "src/theme";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { checkTokenValidity } from "src/utils";

const history = createBrowserHistory();

function App() {
  const themeSeeting = useContext(SettingsContext);
  const theme = createMuiTheme({
    theme: themeSeeting.settings.theme,
  });

  useEffect(() => {
    // Check if token is valid on app load/refresh
    const verifySession = async () => {
      // Only check for protected routes (not login pages)
      const publicRoutes = ["/login", "/", "/forget-password", "/Verify-otp", "/reset-password"];
      const currentPath = window.location.pathname;
      
      if (!publicRoutes.includes(currentPath)) {
        console.log("Verifying session for protected route:", currentPath);
        
        // Get token from storage
        const token = window.sessionStorage.getItem("token");
        if (!token) {
          console.log("No token found, redirecting to login");
          window.location.href = "/login";
          return;
        }
        
        // Validate token with API
        await checkTokenValidity();
      }
    };
    
    // Run immediately when app loads
    verifySession();
    
    // Also set up an interval to periodically check token validity
    const tokenCheckInterval = setInterval(() => {
      const token = window.sessionStorage.getItem("token");
      if (token) {
        checkTokenValidity();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, []);
  
  return (
    <div className="App">
      <ToastContainer />
      <ThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <AuthContext>
            <Router history={history}>
              <RenderRoutes data={routes} />
            </Router>
          </AuthContext>
        </MuiPickersUtilsProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;

function RenderRoutes(props) {
  return (
    <Suspense fallback={<PageLoading />}>
      <Switch>
        {props.data.map((route, i) => {
          const Component = route.component;
          const Guard = route.guard ? AuthGuard : Fragment;
          const Layout = route.layout || Fragment;
          return (
            <Route
              exact={route.exact}
              key={i}
              path={route.path}
              render={(props) => (
                <Guard>
                  <Layout>
                    {route.routes ? (
                      <RenderRoutes data={route.routes} />
                    ) : (
                      <Component {...props} />
                    )}
                  </Layout>
                </Guard>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  );
}
